import { Reducer } from '@reduxjs/toolkit';
import { applyPatches, produceWithPatches, enablePatches, Draft } from 'immer';
import type { InfographState } from 'types/infographTypes';
import { historyStore } from './historyStore';
import { undoActionRegistry } from './undoable';
import { undoAction, redoAction } from './history.actions';
import { isEmpty } from './utils';
import { getPath, hasSimilarPatch } from './history.helpers';
import { HistoryRecord } from './history.types';

enablePatches();

/**
 * Throttle time for adding next same action record
 */
const DEBOUNCE_TIME_MS = 500;

/**
 * This is higher order history reducer that wraps redux slicer
 *
 * @param reducer - A redux reducer
 * @returns The wrap reducer with history manager
 */
export function withHistoryReducer(reducer: Reducer): Reducer {
  // Keep track of last action record for debouncing
  let lastActionRecord: HistoryRecord | null = null;

  return (state, action, ...args) => {
    switch (action.type) {
      case undoAction.type: {
        const record = historyStore.last();
        // If there is no last record or present state = {}, return original state
        if (isEmpty(record) || isEmpty(historyStore.present)) {
          return state;
        }

        const newState = applyPatches(historyStore.present, record.inversePatches);
        historyStore.present = newState;
        historyStore.addFuture(historyStore.pop()!);

        // Push the inverse patches to unsavedPatches
        historyStore.pushUnsavedPatches(
          record.inversePatches.map((patch) => ({
            path: getPath(patch.op === 'remove' ? state : newState, patch),
            patch: patch,
          })),
        );

        return historyStore.present;
      }

      case redoAction.type: {
        const record = historyStore.next();
        if (isEmpty(record)) return state;

        const newState = applyPatches(historyStore.present, record.patches);
        historyStore.present = newState;
        historyStore.addPast(historyStore.shift()!);

        // Push the patches to unsavedPatches
        historyStore.pushUnsavedPatches(
          record.patches.map((patch) => ({
            path: getPath(patch.op === 'remove' ? state : newState, patch),
            patch: patch,
          })),
        );

        return historyStore.present;
      }

      default: {
        // If action not in the registry, fallback default reducer
        // We can implement undo before and after events with pub/sub later on
        if (!undoActionRegistry.has(action.type)) return reducer.apply(null, [state, action, ...args]);

        // Note: following logic can easily swap into snapshot implementation by storing entire nextState in the history
        // Example: historyStore.addPast({ state: nextState, action: action.type});

        /**
         * Use produceWithPatches to retrieve diff between two immer json object
         */
        const [nextState, patches, inversePatches] = produceWithPatches(state as Draft<InfographState>, (draft) =>
          reducer.apply(null, [draft, action, ...args]),
        );

        if (state === nextState) return nextState;

        // Push the patches to unsavedPatches
        historyStore.pushUnsavedPatches(
          patches.map((patch) => ({ path: getPath(patch.op === 'remove' ? state : nextState, patch), patch: patch })),
        );

        const lastRecord = historyStore.last();
        const record = { patches, inversePatches, action: action.type, createdAt: new Date() };

        historyStore.present = nextState as Draft<InfographState>;
        historyStore.clearFuture();

        // Skip adding history record if current action is same as the last record & within threshold
        const isSameAction = lastRecord?.action === action.type;
        const isSimilarPatch = hasSimilarPatch(record, lastRecord);

        let isWithinThreshold = false;
        if (lastRecord) {
          // is the record 500ms after the last record
          isWithinThreshold = record.createdAt.valueOf() - lastRecord.createdAt.valueOf() < DEBOUNCE_TIME_MS;
        }

        if (isSameAction && isSimilarPatch && isWithinThreshold && lastActionRecord?.inversePatches) {
          // The inverse patch should be the same as the first record before isWithinThreshold
          // e.g. dispatch values 0 -> 1 -> 2 -> 3 within 500ms
          // 0 -> 1 (added to history)
          // 1 -> 2 (replace last history but inverse patch should be back to 0 not 1)
          // 2 -> 3 (replace last history but inverse patch should be back to 0 not 2) ...

          record.inversePatches = [...lastActionRecord.inversePatches];
          historyStore.replaceLast(record);
        } else {
          historyStore.addPast(record);
          // keep track of the last action record
          lastActionRecord = record;
        }

        return nextState;
      }
    }
  };
}
